import React, { useState } from 'react';
import {
    GevelbevestigingUnitBasisgegevens,
    GevelbevestigingUnitData,
} from '../../../../../common/src/domain/customInspectionTypes/amsterdamOverspanningen/inspectieOverspanningen';
import {
    CollapsiblePanel,
    EditPanel,
    EditPanelCell,
    EditPanelCellLabel,
    EditPanelFilterSelect,
    EditPanelInput,
    EditPanelRow,
    PanelBody,
    PanelButtonLink,
    Tray,
} from '../../../components/elements';
import { ISurveyTemplateUnitData } from '../../../components/Surveys/ISurveyTemplateUnitData';
import { EditPanelInputNumber } from '../../../components/utils/editPanel/EditPanelInputNumber';
import { useTranslate } from '../../../mixins/translate/translate';
import { BasisgegevensValue } from './components/BasisgegevensValue';
import { EditPanelStraatBagSelect } from './EditPanelStraatBagSelect';
import { EditPanelYesNoEmptyRow } from './EditPanelYesNoEmptyRow';
import { MapDialog } from './MapDialog';
import { SurveyEditPhotosPanel } from './SurveyEditPhotosPanel';
import { useInspectieOverspanningenState } from './useInspectieOverspanningenState';

export function SurveyEditAoInspectieOverspanningenUnitGevelbevestiging(
    props: { surveyId: string; objectId: string; onItemChanged: () => Promise<void> } & ISurveyTemplateUnitData,
) {
    const [isMapVisible, setIsMapVisible] = useState(false);
    const translate = useTranslate();
    const {
        basisgegevensState,
        formState,
        save: saveDatabase,
        setFromState,
    } = useInspectieOverspanningenState<GevelbevestigingUnitBasisgegevens, GevelbevestigingUnitData>(props.surveyId, props.unitId, props.objectId);

    async function save(data: GevelbevestigingUnitData) {
        await saveDatabase(data);
        await props.onItemChanged();
    }

    async function onChange(name: keyof GevelbevestigingUnitData, value: any, shouldSave: boolean) {
        if (!formState) {
            throw new Error('formState is not set');
        }
        const newFormState = { ...formState, [name]: value };
        if (name === 'gevelverbindingGefaald') {
            if (formState.gevelverbindingGefaald === true && newFormState.gevelverbindingGefaald !== true) {
                newFormState.additioneleTrekkrachtWaarbijGevelverbindingFaalde = undefined;
            }
        }
        setFromState(newFormState);
        if (shouldSave) {
            await save(newFormState);
        }
    }
    async function onBlur() {
        if (!formState) {
            throw new Error('formState is not set');
        }
        await save(formState);
    }
    async function onMapClose(newValue?: { latitude: number; longitude: number }) {
        setIsMapVisible(false);
        if (newValue) {
            const newFormState = {
                ...formState,
                latitudeMutatie: newValue.latitude,
                longitudeMutatie: newValue.longitude,
            };
            setFromState(newFormState);
            await save(newFormState);
        }
    }

    if (formState === undefined) {
        return null;
    }

    return (
        <>
            {isMapVisible && <MapDialog onClose={onMapClose} latitude={formState.latitudeMutatie} longitude={formState.longitudeMutatie} />}
            <Tray>
                <EditPanel bottomMargin={true} noPadding={true}>
                    <CollapsiblePanel heading={{ dark: true, text: `${props.unitName} inventarisatie`, initialIsClosed: false }}>
                        <PanelBody isForm={true}>
                            <EditPanelRow>
                                <EditPanelCellLabel rowCount={2} text="Straat (BAG)"></EditPanelCellLabel>
                                <EditPanelCell rowCount={8}>
                                    <BasisgegevensValue value={basisgegevensState.straatbag} />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCell rowCount={2}></EditPanelCell>
                                <EditPanelCell rowCount={8}>
                                    <EditPanelStraatBagSelect
                                        onChange={(value) => void onChange('straatBagMutatie', value ?? undefined, true)}
                                        value={formState.straatBagMutatie}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCellLabel rowCount={2} text="Type gedetailleerd"></EditPanelCellLabel>
                                <EditPanelCell rowCount={8}>
                                    <EditPanelFilterSelect
                                        name="typeGedetailleerdMutatie"
                                        options={[
                                            { text: 'Muurplaat klassiek gietijzer', value: 'Muurplaat klassiek gietijzer' },
                                            { text: 'Muurplaat klassiek RVS316', value: 'Muurplaat klassiek RVS316' },
                                            { text: 'Muurplaat klassiek met katrol', value: 'Muurplaat klassiek met katrol' },
                                            { text: 'Muurplaat modern klein verz. staal', value: 'Muurplaat modern klein verz. staal' },
                                            { text: 'Muurplaat modern klein RVS316', value: 'Muurplaat modern klein RVS316' },
                                            { text: 'Muurplaat modern groot verz. staal', value: 'Muurplaat modern groot verz. staal' },
                                            { text: 'Muurplaat modern groot RVS316', value: 'Muurplaat modern groot RVS316' },
                                            { text: 'Muurplaat vierkant 200x200x5 RVS316', value: 'Muurplaat vierkant 200x200x5 RVS316' },
                                            { text: 'Muurplaat hoekprofiel verz. staal', value: 'Muurplaat hoekprofiel verz. staal' },
                                            { text: 'Muurplaat inbouw RVS', value: 'Muurplaat inbouw RVS' },
                                            { text: 'Muurplaat gevelverlengconstructie', value: 'Muurplaat gevelverlengconstructie' },
                                            { text: 'Bevestigingspunt maatwerk aan constructie', value: 'Bevestigingspunt maatwerk aan constructie' },
                                            { text: 'Schroefoog', value: 'Schroefoog' },
                                            { text: 'Overig: foto toevoegen', value: 'Overig: foto toevoegen' },
                                        ]}
                                        value={formState.typeGedetailleerdMutatie ?? ''}
                                        handleChange={async (value) => onChange('typeGedetailleerdMutatie', value, true)}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCellLabel rowCount={2} text="Huisnummer"></EditPanelCellLabel>
                                <EditPanelCell rowCount={2}>
                                    <EditPanelInput
                                        name="huisnummerMutatie"
                                        value={formState.huisnummerMutatie}
                                        handleChange={async (value) => onChange('huisnummerMutatie', value, false)}
                                        handleBlur={onBlur}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCellLabel rowCount={2} text="Verdieping"></EditPanelCellLabel>
                                <EditPanelCell rowCount={2}>
                                    <EditPanelInput
                                        name="verdiepingMutatie"
                                        value={formState.verdiepingMutatie}
                                        handleChange={async (value) => onChange('verdiepingMutatie', value, false)}
                                        handleBlur={onBlur}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCellLabel rowCount={2} text="Coördinaten"></EditPanelCellLabel>
                                <EditPanelCell rowCount={2}>
                                    <EditPanelInputNumber
                                        name="latitudeMutatie"
                                        value={formState.latitudeMutatie}
                                        onChange={async (value) => onChange('latitudeMutatie', value, false)}
                                        onBlur={onBlur}
                                    />
                                </EditPanelCell>
                                <EditPanelCell rowCount={2}>
                                    <EditPanelInputNumber
                                        name="longitudeMutatie"
                                        value={formState.longitudeMutatie}
                                        onChange={async (value) => onChange('longitudeMutatie', value, false)}
                                        onBlur={onBlur}
                                    />
                                </EditPanelCell>
                                <EditPanelCell rowCount={4}>
                                    <PanelButtonLink
                                        text={translate('object.selectPoint')}
                                        onClick={() => setIsMapVisible(true)}
                                        small
                                        icon="fa fa-globe"
                                    ></PanelButtonLink>
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCellLabel rowCount={2} text="Aanleghoogte"></EditPanelCellLabel>
                                <EditPanelCell rowCount={8}>
                                    <BasisgegevensValue value={basisgegevensState.aanleghoogte} unit="m" />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCell rowCount={2}></EditPanelCell>
                                <EditPanelCell rowCount={2}>
                                    <EditPanelInputNumber
                                        onBlur={onBlur}
                                        name="aanleghoogteMutatie"
                                        value={formState.aanleghoogteMutatie}
                                        onChange={async (value) => onChange('aanleghoogteMutatie', value, false)}
                                        unit="m"
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCellLabel rowCount={2} text="Opmerking"></EditPanelCellLabel>
                                <EditPanelCell rowCount={8}>
                                    <EditPanelInput
                                        name="opmerkingMutatie"
                                        value={formState.opmerkingMutatie}
                                        handleChange={async (value) => onChange('opmerkingMutatie', value, false)}
                                        handleBlur={onBlur}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                        </PanelBody>
                    </CollapsiblePanel>

                    <CollapsiblePanel heading={{ dark: true, text: `${props.unitName} inspectie` }}>
                        <PanelBody isForm={true}>
                            <EditPanelYesNoEmptyRow
                                label="Schade op gevel binnen 1m van gevelbevestiging?"
                                name="schadeOpGevelBinnen1mVanGevelbevestiging"
                                value={formState.schadeOpGevelBinnen1mVanGevelbevestiging}
                                onChange={async (value) => onChange('schadeOpGevelBinnen1mVanGevelbevestiging', value, true)}
                            />
                            <EditPanelYesNoEmptyRow
                                label="Belemmerende/belastende begroeiing bij overspanningsinstallatie?"
                                tooltip="Geef aan of de aangetroffen begroeiing de visuele inspectie en/of het uitvoeren van een trekproef belemmeren. Meld dit ook direct aan de toezichthouder. Begroeiing mag niet zonder tussenkomst van de omgevingsmanager worden verwijderd."
                                name="belemmerendeBelastendeBegroeiingBijOverspanningsinstallatie"
                                value={formState.belemmerendeBelastendeBegroeiingBijOverspanningsinstallatie}
                                onChange={async (value) => onChange('belemmerendeBelastendeBegroeiingBijOverspanningsinstallatie', value, true)}
                            />
                            <EditPanelYesNoEmptyRow
                                label="Schade aan muurplaat?"
                                name="schadeAanMuurplaat"
                                value={formState.schadeAanMuurplaat}
                                onChange={async (value) => onChange('schadeAanMuurplaat', value, true)}
                            />
                            <EditPanelYesNoEmptyRow
                                label="Onjuiste montage?"
                                name="onjuisteMontage"
                                value={formState.onjuisteMontage}
                                onChange={async (value) => onChange('onjuisteMontage', value, true)}
                            />
                            <EditPanelYesNoEmptyRow
                                label="Moer niet volledig over draadeind?"
                                name="moerNietVolledigOverDraadeind"
                                value={formState.moerNietVolledigOverDraadeind}
                                onChange={async (value) => onChange('moerNietVolledigOverDraadeind', value, true)}
                            />
                            <EditPanelYesNoEmptyRow
                                label="Ontbrekende bevestigingsmaterialen?"
                                name="ontbrekendeBevestigingsmaterialen"
                                value={formState.ontbrekendeBevestigingsmaterialen}
                                onChange={async (value) => onChange('ontbrekendeBevestigingsmaterialen', value, true)}
                            />
                            <EditPanelRow>
                                <EditPanelCellLabel
                                    rowCount={4}
                                    text="Totaal kracht"
                                    tooltip="Bepaal met de tabel de waarde (trekkracht) die van toepassing is op de spandraad."
                                ></EditPanelCellLabel>
                                <EditPanelCell rowCount={2}>
                                    <EditPanelInputNumber
                                        onBlur={onBlur}
                                        name="totaalKracht"
                                        value={formState.totaalKracht}
                                        onChange={async (value) => onChange('totaalKracht', value, false)}
                                        unit="N"
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCellLabel
                                    rowCount={4}
                                    text="Gemeten voorspanning"
                                    tooltip="Meet de voorspanning op het spandraad met de voorspanningsmeter en noteer de waarde in Newton."
                                ></EditPanelCellLabel>
                                <EditPanelCell rowCount={2}>
                                    <EditPanelInputNumber
                                        onBlur={onBlur}
                                        name="gemetenVoorspanning"
                                        value={formState.gemetenVoorspanning}
                                        onChange={async (value) => onChange('gemetenVoorspanning', value, false)}
                                        unit="N"
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCellLabel
                                    rowCount={4}
                                    text="Toegepaste additionele trekkracht"
                                    tooltip="De trekproefkracht wordt opgezocht in de kabelberekeningentabel. Breng de gemeten voorspanning in mindering en noteer hier de resulterende kracht. Dit is de kracht die op de gevelbevestiging dient te worden toegepast."
                                ></EditPanelCellLabel>
                                <EditPanelCell rowCount={2}>
                                    <EditPanelInputNumber
                                        onBlur={onBlur}
                                        name="toegepasteAdditioneleTrekkracht"
                                        value={formState.toegepasteAdditioneleTrekkracht}
                                        onChange={async (value) => onChange('toegepasteAdditioneleTrekkracht', value, false)}
                                        unit="N"
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelYesNoEmptyRow
                                label="Gevelverbinding gefaald?"
                                tooltip={`Geef aan of de gevelbevestiging de trekproef succesvol heeft doorstaan. Een "ja" betekent dat de gevelbevestiging wordt afgekeurd op basis van de trekproefresultaten. Voeg foto('s) toe van evt. schade agv de trekproef.`}
                                name="gevelverbindingGefaald"
                                value={formState.gevelverbindingGefaald}
                                onChange={async (value) => onChange('gevelverbindingGefaald', value, true)}
                            />
                            {formState.gevelverbindingGefaald && (
                                <EditPanelRow>
                                    <EditPanelCellLabel
                                        rowCount={4}
                                        text="Additionele trekkracht waarbij gevelverbinding faalde"
                                        tooltip="Geef aan bij welke maximale kracht de trekproef voortijdig is afgebroken in het geval van afkeuren."
                                    ></EditPanelCellLabel>
                                    <EditPanelCell rowCount={2}>
                                        <EditPanelInputNumber
                                            onBlur={onBlur}
                                            name="additioneleTrekkrachtWaarbijGevelverbindingFaalde"
                                            value={formState.additioneleTrekkrachtWaarbijGevelverbindingFaalde}
                                            onChange={async (value) => onChange('additioneleTrekkrachtWaarbijGevelverbindingFaalde', value, false)}
                                            unit="N"
                                        />
                                    </EditPanelCell>
                                </EditPanelRow>
                            )}
                            <EditPanelRow>
                                <EditPanelCellLabel rowCount={4} text="Opmerking"></EditPanelCellLabel>
                                <EditPanelCell rowCount={8}>
                                    <EditPanelInput
                                        handleBlur={onBlur}
                                        name="opmerking"
                                        value={formState.opmerking}
                                        handleChange={async (value) => onChange('opmerking', value, false)}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                        </PanelBody>
                    </CollapsiblePanel>

                    <SurveyEditPhotosPanel
                        objectId={props.objectId}
                        surveyId={props.surveyId}
                        unitName={props.unitName}
                        photos={formState.fotoBestanden}
                        onPhotosChanged={async (photos) => onChange('fotoBestanden', photos, true)}
                    />
                </EditPanel>
            </Tray>
        </>
    );
}
